<template>
  <div class="container mx-auto px-4 py-4">
    <!-- Stats Overview -->
    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
      <div
        class="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 p-6"
      >
        <div class="flex items-center">
          <div class="p-2 bg-blue-100 dark:bg-blue-900/40 rounded-lg">
            <svg
              class="w-6 h-6 text-blue-600 dark:text-blue-300"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
              ></path>
            </svg>
          </div>
          <div class="ml-4">
            <p class="text-sm font-medium text-gray-600 dark:text-gray-300">Total Events</p>
            <p class="text-2xl font-semibold text-gray-900 dark:text-white">
              {{ stats.totalEvents }}
            </p>
          </div>
        </div>
      </div>

      <div
        class="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 p-6"
      >
        <div class="flex items-center">
          <div class="p-2 bg-green-100 dark:bg-green-900/40 rounded-lg">
            <svg
              class="w-6 h-6 text-green-600 dark:text-green-300"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              ></path>
            </svg>
          </div>
          <div class="ml-4">
            <p class="text-sm font-medium text-gray-600 dark:text-gray-300">Total Songs</p>
            <p class="text-2xl font-semibold text-gray-900 dark:text-white">
              {{ stats.totalSongs }}
            </p>
          </div>
        </div>
      </div>

      <div
        class="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 p-6"
      >
        <div class="flex items-center">
          <div class="p-2 bg-purple-100 dark:bg-purple-900/40 rounded-lg">
            <svg
              class="w-6 h-6 text-purple-600 dark:text-purple-300"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
              ></path>
            </svg>
          </div>
          <div class="ml-4">
            <p class="text-sm font-medium text-gray-600 dark:text-gray-300">Total Users</p>
            <p class="text-2xl font-semibold text-gray-900 dark:text-white">
              {{ stats.totalUsers }}
            </p>
          </div>
        </div>
      </div>

      <div
        class="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 p-6"
      >
        <div class="flex items-center">
          <div class="p-2 bg-red-100 dark:bg-red-900/40 rounded-lg">
            <svg
              class="w-6 h-6 text-red-600 dark:text-red-300"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
              ></path>
            </svg>
          </div>
          <div class="ml-4">
            <p class="text-sm font-medium text-gray-600 dark:text-gray-300">Total Votes</p>
            <p class="text-2xl font-semibold text-gray-900 dark:text-white">
              {{ stats.totalVotes }}
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Event Selection -->
    <div
      class="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 p-6 mb-8"
    >
      <div class="flex flex-col md:flex-row md:items-center md:justify-between">
        <div>
          <h2 class="text-xl font-semibold text-gray-900 dark:text-white">Event Analytics</h2>
          <p class="text-gray-600 dark:text-gray-400 mt-1">
            Select an event to view detailed statistics
          </p>
          <div
            v-if="selectedEvent"
            class="mt-3 flex flex-wrap items-center gap-2 text-sm text-gray-600 dark:text-gray-400"
          >
            <span
              :class="getEventTypeBadgeClass(resolveEventType(selectedEvent))"
              class="px-2 py-0.5 text-xs font-semibold rounded-full"
            >
              {{ getEventTypeLabelForEvent(selectedEvent) }}
            </span>
            <span class="font-medium text-gray-900 dark:text-white">{{ selectedEvent.name }}</span>
            <span v-if="selectedEvent.slug" class="text-gray-500 dark:text-gray-400">
              /{{ selectedEvent.slug }}
            </span>
          </div>
        </div>
        <div class="mt-4 md:mt-0">
          <select
            v-model="selectedEventId"
            class="px-4 py-2 border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            @change="fetchEventStats"
          >
            <option value="">Select an event</option>
            <option v-for="event in endedEvents" :key="event.id" :value="event.id">
              {{ formatEventSelectLabel(event) }}
            </option>
          </select>
        </div>
      </div>
    </div>

    <!-- Top 5 Songs Widget (shown when an event is selected, hidden for karaoke) -->
    <div
      v-if="selectedEventId && topSongs.length > 0 && !isKaraokeStats"
      class="mb-6 bg-gradient-to-r from-teal-50 to-blue-50 dark:from-teal-900/20 dark:to-blue-900/20 rounded-xl border border-teal-200 dark:border-teal-800 p-6"
    >
      <div class="flex items-center gap-3 mb-4">
        <div
          class="w-10 h-10 bg-teal-100 dark:bg-teal-900/30 rounded-lg flex items-center justify-center"
        >
          <i class="pi pi-trophy text-teal-600 dark:text-teal-400 text-lg"></i>
        </div>
        <h3 class="text-lg font-semibold text-gray-900 dark:text-white">
          Top {{ topSongs.length }} Songs
        </h3>
      </div>
      <div class="grid grid-cols-1 md:grid-cols-5 gap-3">
        <div
          v-for="(song, index) in topSongs"
          :key="song.id"
          class="bg-white dark:bg-gray-900 rounded-lg p-4 border-2"
          :class="{
            'border-teal-400 dark:border-teal-500': index === 0,
            'border-teal-500 dark:border-teal-600': index === 1,
            'border-teal-600 dark:border-teal-700': index === 2,
            'border-teal-700 dark:border-teal-800': index === 3,
            'border-teal-800 dark:border-teal-900': index === 4
          }"
        >
          <div class="flex items-center gap-2 mb-2">
            <span
              class="text-2xl font-bold"
              :class="{
                'text-teal-400 dark:text-teal-400': index === 0,
                'text-teal-500 dark:text-teal-500': index === 1,
                'text-teal-600 dark:text-teal-600': index === 2,
                'text-teal-700 dark:text-teal-700': index === 3,
                'text-teal-800 dark:text-teal-800': index === 4
              }"
            >
              {{ index + 1 }}
            </span>
            <div class="flex-1 min-w-0">
              <p class="font-semibold text-sm text-gray-900 dark:text-white truncate">
                {{ song.title }}
              </p>
              <p v-if="song.author" class="text-xs text-gray-500 dark:text-gray-400 truncate">
                {{ song.author }}
              </p>
            </div>
          </div>
          <div class="flex items-center gap-1 text-teal-600 dark:text-teal-400">
            <i class="pi pi-heart text-xs"></i>
            <span class="text-sm font-semibold">{{ song.voteCount }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Event Statistics -->
    <div v-if="selectedEventId && eventStats" class="space-y-8">
      <!-- User Activity -->
      <div class="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
        <div class="p-6 border-b border-gray-200 dark:border-gray-700">
          <h3 class="text-xl font-semibold text-gray-900 dark:text-white">User Activity</h3>
        </div>
        <div class="p-6">
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
            <div class="text-center">
              <div class="text-3xl font-bold text-blue-600 dark:text-blue-300">
                {{ eventStats.activeUsers || 0 }}
              </div>
              <div class="text-gray-600 dark:text-gray-300">Logged-in Users</div>
            </div>
            <div class="text-center">
              <div class="text-3xl font-bold text-cyan-600 dark:text-cyan-300">
                {{ eventStats.anonymousDevices || 0 }}
              </div>
              <div class="text-gray-600 dark:text-gray-300">Anonymous Devices</div>
            </div>
            <div class="text-center">
              <div class="text-3xl font-bold text-green-600 dark:text-green-300">
                {{ eventStats.averageVotesPerUser || 0 }}
              </div>
              <div class="text-gray-600 dark:text-gray-300">Avg Votes per User</div>
            </div>
            <div class="text-center">
              <div class="text-3xl font-bold text-teal-600 dark:text-teal-300">
                {{ eventStats.averageVotesPerDevice || 0 }}
              </div>
              <div class="text-gray-600 dark:text-gray-300">Avg Votes per Device</div>
            </div>
            <div class="text-center">
              <div class="text-3xl font-bold text-purple-600 dark:text-purple-300">
                {{ eventStats.mostActiveUser || "-" }}
              </div>
              <div class="text-gray-600 dark:text-gray-300">Most Active User</div>
            </div>
          </div>
        </div>
      </div>

      <!-- Voting Activity Timeline -->
      <div
        v-if="eventStats.voteTimeline && eventStats.voteTimeline.length > 1 && !isKaraokeStats"
        class="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700"
      >
        <div class="p-6 border-b border-gray-200 dark:border-gray-700">
          <div class="flex items-center justify-between">
            <h3 class="text-xl font-semibold text-gray-900 dark:text-white">
              Voting Activity Timeline
            </h3>
            <div class="flex gap-2 flex-wrap items-center">
              <button
                v-for="range in timelineRanges"
                :key="range.label"
                :class="[
                  'px-3 py-1 text-xs font-medium rounded-full border transition-colors',
                  selectedTimelineRange === range.value
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'text-gray-600 dark:text-gray-400 border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800'
                ]"
                @click="selectTimelineRange(range.value)"
              >
                {{ range.label }}
              </button>
              <button
                :class="[
                  'px-3 py-1 text-xs font-medium rounded-full border transition-colors',
                  selectedTimelineRange === 'custom'
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'text-gray-600 dark:text-gray-400 border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800'
                ]"
                @click="selectTimelineRange('custom')"
              >
                <i class="pi pi-search mr-1" style="font-size: 11px"></i>
                Custom
              </button>
              <span class="w-px h-4 bg-gray-300 dark:bg-gray-600 mx-1"></span>
              <button
                :class="[
                  'px-3 py-1 text-xs font-medium rounded-full border transition-colors',
                  showSetOverlay
                    ? 'bg-amber-500 text-white border-amber-500'
                    : 'text-gray-600 dark:text-gray-400 border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800'
                ]"
                @click="showSetOverlay = !showSetOverlay"
              >
                <i class="pi pi-volume-up mr-1" style="font-size: 11px"></i>
                Set
              </button>
              <button
                :class="[
                  'px-3 py-1 text-xs font-medium rounded-full border transition-colors',
                  showSongLabels
                    ? 'bg-teal-500 text-white border-teal-500'
                    : 'text-gray-600 dark:text-gray-400 border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800'
                ]"
                @click="showSongLabels = !showSongLabels"
              >
                <i class="pi pi-list mr-1" style="font-size: 11px"></i>
                Songs
              </button>
            </div>
          </div>
          <!-- Custom time range picker -->
          <div
            v-if="selectedTimelineRange === 'custom'"
            class="flex items-center gap-3 flex-wrap mt-4 px-6 pb-2"
          >
            <label class="text-xs text-gray-500 dark:text-gray-400">From</label>
            <input
              v-model="customTimeFrom"
              type="time"
              class="px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <label class="text-xs text-gray-500 dark:text-gray-400">To</label>
            <input
              v-model="customTimeTo"
              type="time"
              class="px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <button
              v-if="customTimeFrom || customTimeTo"
              class="px-2 py-1 text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
              title="Clear custom range"
              @click="
                customTimeFrom = '';
                customTimeTo = '';
              "
            >
              <i class="pi pi-times"></i>
            </button>
            <span class="text-xs text-gray-400 dark:text-gray-500 ml-1">
              {{ customRangeInfo }}
            </span>
          </div>
        </div>
        <div class="p-6">
          <Line
            :data="timelineChartData"
            :options="timelineChartOptions"
            style="max-height: 360px"
          />
        </div>
      </div>

      <!-- Vote Distribution -->
      <div v-if="!isKaraokeStats" class="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
        <div class="p-6 border-b border-gray-200 dark:border-gray-700">
          <h3 class="text-xl font-semibold text-gray-900 dark:text-white">Vote Distribution</h3>
        </div>
        <div class="p-6">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 class="font-semibold text-gray-900 dark:text-white mb-4">Votes by Genre</h4>
              <div class="space-y-3">
                <div
                  v-for="genre in eventStats.votesByGenre"
                  :key="genre.name"
                  class="flex items-center justify-between"
                >
                  <span class="text-gray-700 dark:text-gray-300">{{ genre.name }}</span>
                  <span class="font-semibold text-blue-600 dark:text-blue-300">{{
                    genre.votes
                  }}</span>
                </div>
              </div>
            </div>
            <div>
              <h4 class="font-semibold text-gray-900 dark:text-white mb-4">Votes by Category</h4>
              <div class="space-y-3">
                <div
                  v-for="category in eventStats.votesByCategory"
                  :key="category.name"
                  class="flex items-center justify-between"
                >
                  <span class="text-gray-700 dark:text-gray-300">{{ category.name }}</span>
                  <span class="font-semibold text-green-600 dark:text-green-300">{{
                    category.votes
                  }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Song Performance History (Concert events only) -->
      <div
        v-if="!hideSongPerformanceHistory"
        class="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700"
      >
        <div class="p-6 border-b border-gray-200 dark:border-gray-700">
          <h3 class="text-xl font-semibold text-gray-900 dark:text-white">
            Song Performance History
          </h3>
        </div>
        <div class="p-6">
          <div class="overflow-x-auto">
            <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-800">
              <thead class="bg-gray-50 dark:bg-gray-900">
                <tr>
                  <th
                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                  >
                    Song
                  </th>
                  <th
                    v-if="!isKaraokeStats"
                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                  >
                    Total Votes
                  </th>
                  <th
                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                  >
                    Start / End Time
                  </th>
                </tr>
              </thead>
              <tbody
                class="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-800"
              >
                <tr
                  v-for="(song, songIdx) in eventStats.songHistory.filter((s) => s.startTime)"
                  :key="`${song.id}-${song.startTime}-${songIdx}`"
                >
                  <td class="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div class="text-sm font-medium text-gray-900 dark:text-white">
                        {{ song.title }}
                      </div>
                      <div class="text-sm text-gray-500 dark:text-gray-400">{{ song.author }}</div>
                      <div
                        v-if="song.performerName"
                        class="text-xs font-medium text-purple-600 dark:text-purple-300 mt-0.5"
                      >
                        <i class="pi pi-microphone mr-1" style="font-size: 10px"></i>{{ song.performerName }}
                      </div>
                    </div>
                  </td>
                  <td
                    v-if="!isKaraokeStats"
                    class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white"
                  >
                    {{ song.totalVotes }}
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                    <div>Start: {{ formatDateTime(song.startTime) }}</div>
                    <div>End: {{ formatDateTime(song.endTime) }}</div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Top Songs -->
      <div v-if="!isKaraokeStats" class="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
        <div class="p-6 border-b border-gray-200 dark:border-gray-700">
          <h3 class="text-xl font-semibold text-gray-900 dark:text-white">Top Songs by Votes</h3>
        </div>
        <div class="p-6">
          <div v-if="eventStats.topSongs.length === 0" class="text-center py-8">
            <p class="text-gray-500 dark:text-gray-400">No votes recorded for this event yet.</p>
          </div>
          <div v-else class="space-y-4">
            <div
              v-for="(song, index) in visibleTopSongs"
              :key="song.id"
              class="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-900 rounded-lg"
            >
              <div class="flex items-center space-x-4">
                <div
                  class="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold"
                >
                  {{ index + 1 }}
                </div>
                <div>
                  <h4 class="font-semibold text-gray-900 dark:text-white">{{ song.title }}</h4>
                  <p class="text-gray-600 dark:text-gray-300">{{ song.author }}</p>
                  <p v-if="song.genre" class="text-sm text-gray-500 dark:text-gray-400">
                    {{ song.genre }}
                  </p>
                </div>
              </div>
              <div class="text-right">
                <div class="text-2xl font-bold text-blue-600 dark:text-blue-300">
                  {{ song.totalVotes }}
                </div>
                <div class="text-sm text-gray-500 dark:text-gray-400">votes</div>
              </div>
            </div>
            <div v-if="topSongsLimit < eventStats.topSongs.length" class="text-center pt-2">
              <button
                class="px-6 py-2 text-sm font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 hover:bg-blue-100 dark:hover:bg-blue-900/50 border border-blue-200 dark:border-blue-800 rounded-lg transition-colors"
                @click="topSongsLimit += 20"
              >
                Load more ({{ eventStats.topSongs.length - topSongsLimit }} remaining)
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="flex justify-center items-center h-64">
      <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed } from "vue";
import api from "../../services/api";
import { useVotesStore } from "../../stores/votes";
import {
  isPrevotingEvent,
  isKaraokeEvent,
  getEventTypeLabelForEvent,
  getEventTypeBadgeClass,
  resolveEventType
} from "../../constants/eventTypes";
import { formatEventSelectLabel } from "../../utils/eventLabel";
import { Line } from "vue-chartjs";
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Filler,
  Tooltip,
  Legend
} from "chart.js";
import annotationPlugin from "chartjs-plugin-annotation";

ChartJS.register(
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Filler,
  Tooltip,
  Legend,
  annotationPlugin
);

export default {
  name: "AdminStats",
  components: { Line },
  setup() {
    const votesStore = useVotesStore();
    const loading = ref(false);
    const stats = ref({
      totalEvents: 0,
      totalSongs: 0,
      totalUsers: 0,
      totalVotes: 0
    });
    const events = ref([]);
    const selectedEventId = ref("");
    const eventStats = ref(null);
    const selectedEventMeta = ref(null);
    const hideSongPerformanceHistory = ref(true);
    const eventSongs = ref([]);
    const topSongsLimit = ref(20);

    const fetchOverallStats = async () => {
      try {
        loading.value = true;
        const response = await api.get("/api/stats/overall");
        if (response.data.success) {
          stats.value = response.data.data;
        }
      } catch (error) {
        console.error("Failed to fetch overall stats:", error);
      } finally {
        loading.value = false;
      }
    };

    const fetchEvents = async () => {
      try {
        const response = await api.get("/api/events");
        if (response.data.success) {
          events.value = (response.data.data.events || []).map((event) => ({
            ...event,
            eventType: resolveEventType(event)
          }));
        }
      } catch (error) {
        console.error("Failed to fetch events:", error);
      }
    };

    const formatDateTime = (dateString) => {
      if (!dateString) return "-";
      return new Date(dateString).toLocaleString();
    };

    // Filter to only show ended events
    const endedEvents = computed(() => {
      const now = new Date();
      return events.value
        .filter((event) => {
          // Event is ended if:
          // 1. It's disabled (manually ended)
          // 2. OR votingEndTime exists and is more than 3 hours ago
          // 3. OR event date has passed (for events without votingEndTime)
          if (event.isDisabled) {
            return true;
          }
          if (event.votingEndTime) {
            const votingEnd = new Date(event.votingEndTime);
            const threeHoursLater = new Date(votingEnd.getTime() + 3 * 60 * 60 * 1000);
            return now > threeHoursLater;
          }
          // If no votingEndTime, check if event start time has passed
          if (event.eventStartTime) {
            const eventStart = new Date(event.eventStartTime);
            return now > eventStart;
          }
          return false;
        })
        .sort((a, b) => {
          // Sort by event start time descending (most recent first)
          const dateA = a.eventStartTime ? new Date(a.eventStartTime) : new Date(0);
          const dateB = b.eventStartTime ? new Date(b.eventStartTime) : new Date(0);
          return dateB - dateA;
        });
    });

    const selectedEvent = computed(() => {
      if (!selectedEventId.value) return null;
      const eventId = Number(selectedEventId.value);
      if (selectedEventMeta.value && Number(selectedEventMeta.value.id) === eventId) {
        return selectedEventMeta.value;
      }
      return events.value.find((event) => Number(event.id) === eventId) || null;
    });

    const updatePerformanceHistoryVisibility = (event, statsEventType) => {
      const eventWithType =
        event && statsEventType
          ? { ...event, eventType: statsEventType }
          : event;
      hideSongPerformanceHistory.value = isPrevotingEvent(eventWithType);
    };

    const isKaraokeStats = computed(() =>
      isKaraokeEvent(
        eventStats.value
          ? { eventType: eventStats.value.eventType }
          : null
      )
    );

    const fetchEventStats = async () => {
      if (!selectedEventId.value) {
        eventStats.value = null;
        selectedEventMeta.value = null;
        hideSongPerformanceHistory.value = true;
        eventSongs.value = [];
        return;
      }

      topSongsLimit.value = 20;

      try {
        loading.value = true;
        const eventId = selectedEventId.value;

        const [statsResponse, eventResponse] = await Promise.all([
          api.get(`/api/stats/event/${eventId}`),
          api.get(`/api/events/${eventId}`)
        ]);

        if (eventResponse.data.success) {
          selectedEventMeta.value = eventResponse.data.data.event;
        } else {
          selectedEventMeta.value =
            events.value.find((event) => Number(event.id) === Number(eventId)) || null;
        }

        const selectedEvent =
          selectedEventMeta.value ||
          events.value.find((event) => Number(event.id) === Number(eventId));

        if (statsResponse.data.success) {
          eventStats.value = statsResponse.data.data;
        }

        updatePerformanceHistoryVisibility(
          selectedEvent,
          statsResponse.data.success ? statsResponse.data.data.eventType : null
        );

        if (selectedEvent && statsResponse.data.success && statsResponse.data.data?.eventType) {
          const resolvedType = resolveEventType({
            ...selectedEvent,
            eventType: statsResponse.data.data.eventType
          });
          const listIndex = events.value.findIndex(
            (event) => Number(event.id) === Number(eventId)
          );
          if (listIndex !== -1 && events.value[listIndex].eventType !== resolvedType) {
            events.value[listIndex] = {
              ...events.value[listIndex],
              eventType: resolvedType
            };
          }
        }

        // Fetch songs for the event
        const songsResponse = await api.get("/api/songs");
        if (songsResponse.data.success) {
          eventSongs.value = (songsResponse.data.data.songs || []).filter(
            (song) => !song.isDisabled
          );
        }

        // Fetch votes for the selected event
        await votesStore.fetchEventVotes(selectedEventId.value);
      } catch (error) {
        console.error("Failed to fetch event stats:", error);
      } finally {
        loading.value = false;
      }
    };

    const getVoteCount = (songId) => {
      if (!selectedEventId.value) return 0;
      return votesStore.getSongVoteCount(selectedEventId.value, songId);
    };

    // Top 5 songs for the selected event
    const topSongs = computed(() => {
      if (!selectedEventId.value || eventSongs.value.length === 0) return [];

      const songsWithVotes = eventSongs.value.map((song) => ({
        ...song,
        voteCount: getVoteCount(song.id)
      }));

      return songsWithVotes
        .filter((song) => song.voteCount > 0)
        .sort((a, b) => b.voteCount - a.voteCount)
        .slice(0, 5);
    });

    const formatDate = (dateString) => {
      return new Date(dateString).toLocaleDateString();
    };

    const visibleTopSongs = computed(() => {
      if (!eventStats.value || !eventStats.value.topSongs) return [];
      return eventStats.value.topSongs.slice(0, topSongsLimit.value);
    });

    // Timeline chart
    const selectedTimelineRange = ref("all");
    const customTimeFrom = ref("");
    const customTimeTo = ref("");
    const timelineRanges = [
      { label: "All", value: "all" },
      { label: "First hour", value: "1h" },
      { label: "First 2h", value: "2h" },
      { label: "First 3h", value: "3h" }
    ];

    const selectTimelineRange = (value) => {
      selectedTimelineRange.value = value;
      if (value !== "custom") {
        customTimeFrom.value = "";
        customTimeTo.value = "";
      }
    };

    // Info text shown below the custom time pickers
    const customRangeInfo = computed(() => {
      if (
        !eventStats.value ||
        !eventStats.value.voteTimeline ||
        eventStats.value.voteTimeline.length === 0
      )
        return "";
      const timeline = eventStats.value.voteTimeline;
      const first = new Date(timeline[0].timestamp);
      const last = new Date(timeline[timeline.length - 1].timestamp);
      const fmt = (d) => d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
      return `Data available: ${fmt(first)} – ${fmt(last)}`;
    });

    // Dynamic bucketing helper — takes an array of epoch-ms timestamps
    // and returns { timestamp, count }[] with resolution adapted to the span
    const bucketTimestamps = (epochMs) => {
      if (epochMs.length === 0) return [];
      const first = epochMs[0];
      const last = epochMs[epochMs.length - 1];
      const spanMs = last - first;

      let bucketMs;
      if (spanMs <= 15 * 60 * 1000)
        bucketMs = 15 * 1000; // ≤ 15 min → 15 s
      else if (spanMs <= 30 * 60 * 1000)
        bucketMs = 30 * 1000; // ≤ 30 min → 30 s
      else if (spanMs <= 60 * 60 * 1000)
        bucketMs = 60 * 1000; // ≤ 1 h   → 1 min
      else if (spanMs <= 3 * 60 * 60 * 1000)
        bucketMs = 5 * 60 * 1000; // ≤ 3 h   → 5 min
      else if (spanMs <= 12 * 60 * 60 * 1000)
        bucketMs = 15 * 60 * 1000; // ≤ 12 h  → 15 min
      else bucketMs = 60 * 60 * 1000; // > 12 h  → 1 h

      const counts = {};
      epochMs.forEach((t) => {
        const bucket = Math.floor((t - first) / bucketMs) * bucketMs + first;
        counts[bucket] = (counts[bucket] || 0) + 1;
      });

      const result = [];
      for (let t = first; t <= last; t += bucketMs) {
        const bucket = Math.floor((t - first) / bucketMs) * bucketMs + first;
        result.push({ timestamp: new Date(bucket).toISOString(), count: counts[bucket] || 0 });
      }
      return result;
    };

    const filteredTimeline = computed(() => {
      if (!eventStats.value) return [];

      const rawTs = eventStats.value.voteTimestamps;
      const hasRaw = rawTs && rawTs.length > 0;

      // For "all" — use the pre-computed server timeline (fast)
      if (selectedTimelineRange.value === "all") {
        return eventStats.value.voteTimeline || [];
      }

      // For zoomed views — re-bucket from raw timestamps for higher resolution
      if (hasRaw) {
        let filtered;
        if (selectedTimelineRange.value === "custom") {
          if (!customTimeFrom.value && !customTimeTo.value) {
            filtered = rawTs;
          } else {
            filtered = rawTs.filter((ts) => {
              const hhmm = new Date(ts).toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
                hour12: false
              });
              if (customTimeFrom.value && hhmm < customTimeFrom.value) return false;
              if (customTimeTo.value && hhmm > customTimeTo.value) return false;
              return true;
            });
          }
        } else {
          const hours = parseInt(selectedTimelineRange.value);
          const firstTs = new Date(rawTs[0]).getTime();
          const cutoff = firstTs + hours * 60 * 60 * 1000;
          filtered = rawTs.filter((ts) => new Date(ts).getTime() <= cutoff);
        }
        return bucketTimestamps(filtered.map((ts) => new Date(ts).getTime()));
      }

      // Fallback — no raw data, slice the pre-computed timeline
      const timeline = eventStats.value.voteTimeline || [];
      if (timeline.length === 0) return [];

      if (selectedTimelineRange.value === "custom") {
        if (!customTimeFrom.value && !customTimeTo.value) return timeline;
        return timeline.filter((p) => {
          const hhmm = new Date(p.timestamp).toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
            hour12: false
          });
          if (customTimeFrom.value && hhmm < customTimeFrom.value) return false;
          if (customTimeTo.value && hhmm > customTimeTo.value) return false;
          return true;
        });
      }

      const hours = parseInt(selectedTimelineRange.value);
      const firstTs = new Date(timeline[0].timestamp).getTime();
      const cutoff = firstTs + hours * 60 * 60 * 1000;
      return timeline.filter((p) => new Date(p.timestamp).getTime() <= cutoff);
    });

    const timelineChartData = computed(() => {
      const data = filteredTimeline.value;
      // Detect if we need seconds-level labels (buckets < 1 minute apart)
      const needsSeconds =
        data.length >= 2 &&
        new Date(data[1].timestamp).getTime() - new Date(data[0].timestamp).getTime() < 60000;
      return {
        labels: data.map((p) => {
          const d = new Date(p.timestamp);
          return needsSeconds
            ? d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })
            : d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
        }),
        datasets: [
          {
            label: "Votes",
            data: data.map((p) => p.count),
            borderColor: "#0d9488",
            backgroundColor: "rgba(13, 148, 136, 0.15)",
            fill: true,
            tension: 0.3,
            pointRadius: data.length > 60 ? 0 : 3,
            pointHoverRadius: 5,
            borderWidth: 2
          }
        ]
      };
    });

    // Build annotation objects from songHistory to overlay on the timeline
    const timelineAnnotations = computed(() => {
      if (!eventStats.value || !eventStats.value.songHistory || !filteredTimeline.value.length)
        return {};

      const performed = eventStats.value.songHistory.filter((s) => s.startTime);
      if (performed.length === 0) return {};

      const data = filteredTimeline.value;
      const timestamps = data.map((p) => new Date(p.timestamp).getTime());

      // Visible time window — used to skip songs/sets outside the view
      const visibleStart = timestamps[0];
      const visibleEnd = timestamps[timestamps.length - 1];
      // Allow a small margin so songs right at the edge still appear
      const margin = (visibleEnd - visibleStart) * 0.02;

      // Find the closest label index for a given ISO time
      const findIndex = (isoTime) => {
        const t = new Date(isoTime).getTime();
        let closest = 0;
        let minDiff = Infinity;
        for (let i = 0; i < timestamps.length; i++) {
          const diff = Math.abs(timestamps[i] - t);
          if (diff < minDiff) {
            minDiff = diff;
            closest = i;
          }
        }
        return closest;
      };

      const annotations = {};
      const isDark = document.documentElement.classList.contains("dark");

      // Group songs into sets based on gaps between them.
      const GAP_THRESHOLD_MS = 10 * 60 * 1000; // 10 minutes
      const sets = [];
      let currentSet = null;

      performed.forEach((song) => {
        const songStart = new Date(song.startTime).getTime();
        const songEnd = song.endTime ? new Date(song.endTime).getTime() : songStart;

        if (!currentSet) {
          currentSet = { songs: [song], startTime: songStart, endTime: songEnd };
        } else {
          const gap = songStart - currentSet.endTime;
          if (gap > GAP_THRESHOLD_MS) {
            sets.push(currentSet);
            currentSet = { songs: [song], startTime: songStart, endTime: songEnd };
          } else {
            currentSet.songs.push(song);
            currentSet.endTime = Math.max(currentSet.endTime, songEnd);
          }
        }
      });
      if (currentSet) sets.push(currentSet);

      // Shaded region for each set — only if it overlaps the visible window
      const setColors = [
        {
          bg: isDark ? "rgba(251, 191, 36, 0.08)" : "rgba(251, 191, 36, 0.12)",
          text: isDark ? "#fbbf24" : "#b45309"
        },
        {
          bg: isDark ? "rgba(99, 102, 241, 0.08)" : "rgba(99, 102, 241, 0.12)",
          text: isDark ? "#818cf8" : "#4338ca"
        },
        {
          bg: isDark ? "rgba(16, 185, 129, 0.08)" : "rgba(16, 185, 129, 0.12)",
          text: isDark ? "#34d399" : "#047857"
        },
        {
          bg: isDark ? "rgba(244, 63, 94, 0.08)" : "rgba(244, 63, 94, 0.12)",
          text: isDark ? "#fb7185" : "#be123c"
        }
      ];

      sets.forEach((set, i) => {
        // Skip sets entirely outside the visible window
        if (set.endTime < visibleStart - margin || set.startTime > visibleEnd + margin) return;

        const startIdx = findIndex(new Date(Math.max(set.startTime, visibleStart)).toISOString());
        const endIdx = findIndex(new Date(Math.min(set.endTime, visibleEnd)).toISOString());
        const colors = setColors[i % setColors.length];
        const label = sets.length === 1 ? "Live set" : `Set ${i + 1}`;

        annotations[`set_${i}`] = {
          type: "box",
          xMin: startIdx,
          xMax: endIdx,
          backgroundColor: colors.bg,
          borderWidth: 0,
          label: {
            display: true,
            content: label,
            position: "start",
            color: colors.text,
            font: { size: 11, weight: "bold" },
            padding: 4
          }
        };
      });

      // Vertical line for each performed song — only if it falls within the visible window
      const songColors = [
        "#ef4444",
        "#f97316",
        "#eab308",
        "#22c55e",
        "#3b82f6",
        "#8b5cf6",
        "#ec4899",
        "#14b8a6"
      ];
      let visibleSongIdx = 0;
      performed.forEach((song, i) => {
        const songStartMs = new Date(song.startTime).getTime();
        // Skip songs outside the visible range
        if (songStartMs < visibleStart - margin || songStartMs > visibleEnd + margin) return;

        const idx = findIndex(song.startTime);
        const color = songColors[i % songColors.length];
        // Stagger labels: alternate between two y positions
        const yAdjust = visibleSongIdx % 2 === 0 ? 20 : 35;
        visibleSongIdx++;
        annotations[`song_${i}`] = {
          type: "line",
          xMin: idx,
          xMax: idx,
          borderColor: color,
          borderWidth: 1.5,
          borderDash: [4, 3],
          label: {
            display: true,
            content: song.title.length > 20 ? song.title.substring(0, 18) + "…" : song.title,
            position: "end",
            yAdjust: yAdjust,
            backgroundColor: color,
            color: "#fff",
            font: { size: 9, weight: "bold" },
            padding: { top: 1, bottom: 1, left: 4, right: 4 },
            borderRadius: 3,
            z: 10 + visibleSongIdx
          }
        };
      });

      return annotations;
    });

    // Toggle for showing set overlay
    const showSetOverlay = ref(true);
    const showSongLabels = ref(true);

    const timelineChartOptions = computed(() => ({
      responsive: true,
      maintainAspectRatio: false,
      layout: {
        padding: { top: 10 }
      },
      interaction: {
        intersect: false,
        mode: "index"
      },
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            title: (items) => {
              if (!items.length) return "";
              const idx = items[0].dataIndex;
              const point = filteredTimeline.value[idx];
              if (!point) return "";
              return new Date(point.timestamp).toLocaleString();
            },
            label: (item) => `${item.raw} vote${item.raw !== 1 ? "s" : ""}`
          }
        },
        annotation: {
          annotations: (() => {
            const all = timelineAnnotations.value;
            if (showSetOverlay.value && showSongLabels.value) return all;
            if (!showSetOverlay.value && !showSongLabels.value) return {};
            const filtered = {};
            Object.entries(all).forEach(([key, val]) => {
              if (key.startsWith("set_") && showSetOverlay.value) filtered[key] = val;
              if (key.startsWith("song_") && showSongLabels.value) filtered[key] = val;
            });
            return filtered;
          })()
        }
      },
      scales: {
        x: {
          ticks: {
            maxTicksLimit: 12,
            color: document.documentElement.classList.contains("dark") ? "#9ca3af" : "#6b7280"
          },
          grid: { display: false }
        },
        y: {
          beginAtZero: true,
          suggestedMax: (() => {
            const data = filteredTimeline.value;
            if (!data.length) return 10;
            const max = Math.max(...data.map((p) => p.count));
            // Only add headroom when song labels are visible (they sit at the top)
            return Math.ceil(max * (showSongLabels.value ? 1.2 : 1.05));
          })(),
          ticks: {
            precision: 0,
            color: document.documentElement.classList.contains("dark") ? "#9ca3af" : "#6b7280"
          },
          grid: {
            color: document.documentElement.classList.contains("dark")
              ? "rgba(75,85,99,0.3)"
              : "rgba(229,231,235,0.8)"
          }
        }
      }
    }));

    onMounted(async () => {
      await Promise.all([fetchOverallStats(), fetchEvents()]);
    });

    return {
      loading,
      stats,
      events,
      endedEvents,
      selectedEvent,
      selectedEventId,
      eventStats,
      fetchEventStats,
      formatDate,
      formatDateTime,
      topSongs,
      topSongsLimit,
      visibleTopSongs,
      hideSongPerformanceHistory,
      isKaraokeStats,
      getEventTypeLabelForEvent,
      getEventTypeBadgeClass,
      resolveEventType,
      formatEventSelectLabel,
      selectedTimelineRange,
      timelineRanges,
      selectTimelineRange,
      customTimeFrom,
      customTimeTo,
      customRangeInfo,
      showSetOverlay,
      showSongLabels,
      timelineChartData,
      timelineChartOptions
    };
  }
};
</script>
